import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, ShieldAlert, X } from "lucide-react";

/**
 * ConfirmDialog
 *
 * A HubSpot-style typed confirmation for destructive, irreversible
 * actions. The confirm button stays disabled until the person types the
 * exact confirmation word, so a stray click can never fire the action.
 *
 * Controlled: the caller owns `open` and passes `onCancel` / `onConfirm`.
 * `onConfirm` may be async; while it runs the dialog shows a busy state
 * and blocks a second submit.
 *
 * Props:
 *   open          boolean
 *   onCancel      () => void
 *   onConfirm     () => void | Promise<void>
 *   title         string
 *   description   node        (the plain-language why)
 *   consequences  string[]    (optional: what will happen, as bullets)
 *   confirmWord   string      (the word the person must type; default "DELETE")
 *   confirmLabel  string      (the confirm button label)
 *   tone          "danger" | "warning"   (default "danger")
 *   testId        string      (prefix for data-testids; default "confirm")
 */
export default function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  consequences = [],
  confirmWord = "DELETE",
  confirmLabel = "Confirm",
  tone = "danger",
  testId = "confirm",
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setBusy(false);
      setError("");
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Exact, case-sensitive match (trimmed), mirroring the HubSpot pattern
  // where the person must type the confirmation word exactly as shown.
  const matched = typed.trim() === String(confirmWord).trim();

  const cancel = useCallback(() => {
    if (busy) return;
    onCancel?.();
  }, [busy, onCancel]);

  const confirm = useCallback(async () => {
    if (!matched || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm?.();
    } catch (e) {
      setBusy(false);
      setError(e?.response?.data?.detail || e?.message || "That did not go through. Try again.");
    }
  }, [matched, busy, onConfirm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") cancel();
      if (e.key === "Enter" && matched && !busy) confirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, matched, busy, cancel, confirm]);

  if (!open) return null;

  const accent = tone === "warning" ? "hsl(38 92% 40%)" : "hsl(var(--destructive))";
  const accentSoft = tone === "warning" ? "hsl(38 92% 40% / 0.12)" : "hsl(var(--destructive) / 0.12)";
  const Icon = tone === "warning" ? ShieldAlert : AlertTriangle;

  return (
    <div
      className="k-session-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={`${testId}-dialog`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}
    >
      <div className="k-session-card k-fade-in" style={{ maxWidth: "30rem" }}>
        <div className="flex items-start justify-between gap-3">
          <div
            className="k-session-icon"
            style={{ color: accent, background: accentSoft, marginBottom: 0 }}
          >
            <Icon size={20} />
          </div>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="k-btn k-btn-ghost !p-1.5 !px-1.5 -mr-1 -mt-1"
            aria-label="close"
            data-testid={`${testId}-close`}
          >
            <X size={16} />
          </button>
        </div>

        <h2
          id={`${testId}-title`}
          className="klareet-serif mt-3"
          style={{ fontSize: "1.4rem", lineHeight: 1.2, color: "hsl(var(--klareet-ink))" }}
          data-testid={`${testId}-title`}
        >
          {title}
        </h2>

        {description && (
          <p className="k-quiet mt-2" style={{ lineHeight: 1.55 }} data-testid={`${testId}-description`}>
            {description}
          </p>
        )}

        {consequences.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid={`${testId}-consequences`}>
            {consequences.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: accent }}
                  aria-hidden="true"
                />
                <span style={{ opacity: 0.85 }}>{c}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4">
          <label className="k-label mb-1 block" htmlFor={`${testId}-input`}>
            Type <span className="klareet-mono" style={{ color: accent, fontWeight: 600 }}>{confirmWord}</span> to confirm
          </label>
          <input
            id={`${testId}-input`}
            ref={inputRef}
            className="k-input klareet-mono"
            value={typed}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmWord}
            data-testid={`${testId}-input`}
            style={matched ? { borderColor: accent } : undefined}
          />
        </div>

        {error && (
          <div className="text-sm mt-2" style={{ color: "hsl(var(--destructive))" }} data-testid={`${testId}-error`}>
            {error}
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="k-btn k-btn-ghost sm:flex-none"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!matched || busy}
            className="k-btn sm:flex-none"
            data-testid={`${testId}-confirm`}
            style={{
              background: matched && !busy ? accent : "hsl(var(--klareet-line-soft))",
              color: matched && !busy ? "white" : "hsl(var(--klareet-muted))",
              cursor: matched && !busy ? "pointer" : "not-allowed",
              transition: "background-color 160ms ease, color 160ms ease",
            }}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {busy ? "Working" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
